// MigSidebar.jsx — the context-swapping app sidebar.
//
// Port of design_handoff_customer_tools/mig-sidebar-v2.jsx (+ the nav
// primitives from nav-shared.jsx), styled from T.* tokens. Two modes share
// one navy chrome:
//   · Global mode (inProject=false): Projects / Dashboard / Activity
//   · Project mode (inProject=true): "All projects" back link, a customer
//     summary card, and a TOOLS section listing that customer's tools
// The admin gear sits beside the profile block (admin roles only) and is
// the nav's only admin entry point. Collapsible to a 56px icon rail
// (160ms ease-out); the preference persists per user via localStorage.

import { Fragment, useState } from "react";
import { T } from "../theme";
import { hasFeature } from "../db";
import logoWhite from "../assets/fmx-logo-white.svg";

// Global (cross-customer) surfaces.
export const NAV_GLOBAL = [
  { id: "projects",  icon: "folder-open",       label: "Projects" },
  { id: "dashboard", icon: "gauge-high",        label: "Dashboard" },
  { id: "activity",  icon: "clock-rotate-left", label: "Activity" },
];

// Per-customer tools. `flag` is the profiles.feature_permissions key that
// governs visibility (null = always on). Ids match App's projectTab values;
// the wizard steps roll up under "Data migration" (the caller keeps
// `current` on 'migration' for any wizard screen).
export const PROJECT_TOOLS = [
  { id: "migration",    icon: "right-left",           label: "Data migration",                 flag: null },
  { id: "ocr",          icon: "toolbox",              label: "Existing Attachment Field Scan", flag: "equipment_ocr" },
  { id: "qbr",          icon: "file-powerpoint",      label: "QBR decks",                      flag: "qbr_generator" },
  { id: "fieldBuilder", icon: "wand-magic-sparkles",  label: "AI Form Builder",                flag: "custom_field_builder" },
];

// Admins see every tool regardless of flags (hasFeature handles that).
export function visibleTools(profile) {
  return PROJECT_TOOLS.filter((t) => !t.flag || hasFeature(profile, t.flag));
}

const SIDEBAR_COLLAPSE_KEY = "fmxSidebarCollapsed";

function readCollapsed() {
  try { return localStorage.getItem(SIDEBAR_COLLAPSE_KEY) === "1"; } catch { return false; }
}

function NItem({ icon, label, active, onClick, collapsed, note }) {
  return (
    <a className={"nitem" + (active ? " active" : "")} title={collapsed ? label : undefined} onClick={onClick} style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: collapsed ? "9px 0" : "8px 16px 8px 13px",
      justifyContent: collapsed ? "center" : "flex-start",
      font: `500 14px/20px ${T.fontSans}`, color: active ? T.white : "rgba(255,255,255,0.75)",
      textDecoration: "none", cursor: "pointer",
      borderLeft: collapsed ? "3px solid transparent" : `3px solid ${active ? T.cyan : "transparent"}`,
      borderRight: collapsed ? "3px solid transparent" : "none",
    }}>
      <i className={`fa-solid fa-${icon}`} style={{ width: 16, textAlign: "center", fontSize: 13, color: active ? T.cyan : undefined }} />
      {!collapsed && <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>}
      {!collapsed && note != null && <span style={{ font: `500 11px/14px ${T.fontSans}`, color: "rgba(255,255,255,0.45)", whiteSpace: "nowrap" }}>{note}</span>}
    </a>
  );
}

function NSectionLabel({ children, collapsed }) {
  if (collapsed) return <div style={{ height: 1, background: "rgba(255,255,255,0.12)", margin: "10px 12px" }} />;
  return (
    <div style={{ padding: "16px 16px 6px", color: "rgba(255,255,255,0.5)", font: `700 12px/14px ${T.fontSans}`, textTransform: "uppercase", letterSpacing: "0.07em", whiteSpace: "nowrap" }}>
      {children}
    </div>
  );
}

function NLogoRow({ collapsed, onToggle }) {
  return (
    <div style={{ padding: collapsed ? "8px 0 10px" : "8px 12px 12px 16px", display: "flex", alignItems: "center", gap: 10, justifyContent: collapsed ? "center" : "flex-start", flexDirection: collapsed ? "column" : "row" }}>
      <img src={logoWhite} alt="FMX" style={{ width: 26 }} />
      {!collapsed && <span style={{ font: `700 17px/22px ${T.fontSans}`, color: T.white }}>FMX</span>}
      {!collapsed && <span style={{ font: `700 9px/14px ${T.fontSans}`, color: T.navy, background: T.cyan, padding: "1px 6px", borderRadius: T.radiusPill, letterSpacing: "0.03em", textTransform: "uppercase" }}>Migrate</span>}
      <button className="ncollapse" title={collapsed ? "Expand sidebar" : "Collapse sidebar"} onClick={onToggle} style={{
        marginLeft: collapsed ? 0 : "auto", marginTop: collapsed ? 6 : 0,
        width: 24, height: 24, border: 0, borderRadius: T.radiusBtn, background: "transparent",
        color: "rgba(255,255,255,0.55)", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center",
      }}>
        <i className={`fa-solid fa-angles-${collapsed ? "right" : "left"}`} style={{ fontSize: 11 }} />
      </button>
    </div>
  );
}

// Project-mode summary card: customer name, site, entity progress meter,
// rows pushed. Collapsed: a 30×30 initial tile with tooltip.
function CustomerCard({ customer, collapsed }) {
  if (!customer) return null;
  const { name, site, done = 0, total = 0, rowsPushed = 0 } = customer;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const init = (name || "?").trim().charAt(0).toUpperCase() || "?";
  if (collapsed) {
    return (
      <div title={name} style={{ width: 30, height: 30, margin: "6px auto", borderRadius: 6, background: "rgba(255,255,255,0.12)", color: T.white, display: "flex", alignItems: "center", justifyContent: "center", font: `700 13px ${T.fontSans}` }}>{init}</div>
    );
  }
  return (
    <div style={{ margin: "4px 12px", padding: "10px 12px", background: "rgba(255,255,255,0.07)", borderRadius: 6 }}>
      <div style={{ font: `700 13px/17px ${T.fontSans}`, color: T.white, overflow: "hidden", textOverflow: "ellipsis" }}>{name}</div>
      <div style={{ font: `400 11px/15px ${T.fontSans}`, color: "rgba(255,255,255,0.55)", marginBottom: 8, overflow: "hidden", textOverflow: "ellipsis" }}>{site || "—"}</div>
      <div style={{ height: 5, borderRadius: T.radiusPill, background: "rgba(255,255,255,0.15)", overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: T.cyan, borderRadius: T.radiusPill }} />
      </div>
      <div style={{ font: `500 10px/14px ${T.fontSans}`, color: "rgba(255,255,255,0.55)", marginTop: 5 }}>
        {done} of {total} entities · {(rowsPushed || 0).toLocaleString()} rows pushed
      </div>
    </div>
  );
}

// Bottom block: profile row with the admin gear sitting NEXT to it.
function NProfile({ user, isAdmin, adminActive, onAdmin, collapsed }) {
  const name = user?.name || "User";
  const title = user?.title || "";
  const initials = name.split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "FX";
  const gear = isAdmin && (
    <button className={"ngear" + (adminActive ? " active" : "")} title="Admin settings" onClick={onAdmin} style={{
      width: 28, height: 28, border: 0, borderRadius: T.radiusBtn, flexShrink: 0,
      background: adminActive ? "rgba(255,255,255,0.14)" : "transparent",
      color: adminActive ? T.cyan : "rgba(255,255,255,0.6)",
      cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center",
    }}>
      <i className="fa-solid fa-gear" style={{ fontSize: 14 }} />
    </button>
  );
  if (collapsed) {
    return (
      <div style={{ padding: "10px 0 12px", display: "flex", flexDirection: "column", alignItems: "center", gap: 10, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
        {gear}
        <div title={title ? `${name} · ${title}` : name} style={{ width: 28, height: 28, borderRadius: T.radiusPill, background: T.cyan, color: T.navy, display: "flex", alignItems: "center", justifyContent: "center", font: `700 11px ${T.fontSans}` }}>{initials}</div>
      </div>
    );
  }
  return (
    <div style={{ padding: "11px 12px 12px 16px", display: "flex", alignItems: "center", gap: 10, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
      <div style={{ width: 28, height: 28, borderRadius: T.radiusPill, background: T.cyan, color: T.navy, display: "flex", alignItems: "center", justifyContent: "center", font: `700 11px ${T.fontSans}`, flexShrink: 0 }}>{initials}</div>
      <div style={{ lineHeight: 1.2, flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: T.white, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{name}</div>
        {title && <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</div>}
      </div>
      {gear}
    </div>
  );
}

// customer: { name, site, done, total, rowsPushed } (project mode only)
// tools:    [{ id, icon, label, note? }] — pre-filtered by visibleTools()
// adminActive: admin screen open — only the gear highlights, no nav item
export default function MigSidebar({
  inProject = false, customer, tools = [], current, adminActive = false,
  onNavigate, onBack, onAdmin, user, isAdmin = false,
}) {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const toggle = () => {
    setCollapsed((c) => {
      try { localStorage.setItem(SIDEBAR_COLLAPSE_KEY, c ? "0" : "1"); } catch { /* private mode */ }
      return !c;
    });
  };

  return (
    <nav className="mig-nav" style={{
      width: collapsed ? 56 : 218, background: T.navy, color: T.white, padding: "10px 0 0",
      display: "flex", flexDirection: "column", flexShrink: 0, height: "100%",
      overflow: "hidden", transition: "width 160ms ease-out",
      font: `500 14px/20px ${T.fontSans}`, whiteSpace: "nowrap",
    }}>
      <style>{`
        .mig-nav .nitem.active { background: rgba(255,255,255,0.10); }
        .mig-nav .nitem:not(.active):hover { background: rgba(255,255,255,0.06); }
        .mig-nav .ngear:not(.active):hover { background: rgba(255,255,255,0.10) !important; }
        .mig-nav .ncollapse:hover { background: rgba(255,255,255,0.12) !important; color: #fff !important; }
      `}</style>
      <NLogoRow collapsed={collapsed} onToggle={toggle} />
      {!inProject && NAV_GLOBAL.map((g) => (
        <NItem key={g.id} icon={g.icon} label={g.label} collapsed={collapsed}
          active={!adminActive && current === g.id}
          onClick={() => onNavigate && onNavigate(g.id)} />
      ))}
      {inProject && (
        <Fragment>
          <NItem icon="arrow-left" label="All projects" collapsed={collapsed} onClick={onBack} />
          <CustomerCard customer={customer} collapsed={collapsed} />
          <NSectionLabel collapsed={collapsed}>Tools</NSectionLabel>
          {tools.map((t) => (
            <NItem key={t.id} icon={t.icon} label={t.label} collapsed={collapsed} note={t.note}
              active={!adminActive && current === t.id}
              onClick={() => onNavigate && onNavigate(t.id)} />
          ))}
        </Fragment>
      )}
      <div style={{ flex: 1 }} />
      <NProfile user={user} isAdmin={isAdmin} adminActive={adminActive} onAdmin={onAdmin} collapsed={collapsed} />
    </nav>
  );
}
